import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography, Autocomplete } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarEmplazamientosPPE = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos los códigos originales (llave compuesta) por si el usuario los modifica
  const [centcodigoViejo, setCentcodigoViejo] = useState("")
  const [emplcodigoViejo, setEmplcodigoViejo] = useState("")

  // Estado del formulario
  const [formData, setFormData] = useState({
    centcodigo: "",
    emplcodigo: "",
    empldescri: "",
    emplstatus: "A",
  })

  useEffect(() => {
    // Precargar los inputs con los valores de la fila enviada desde la grilla
    if (state) {
      setFormData({
        centcodigo: state.centcodigo || "",
        emplcodigo: state.emplcodigo || "",
        empldescri: state.empldescri || "",
        emplstatus: state.emplstatus || "A",
      })
      setCentcodigoViejo(state.centcodigo || "")
      setEmplcodigoViejo(state.emplcodigo || "")
    } else {
      navigate(-1) // Retorno de seguridad si entran directo a la URL
    }
  }, [state, navigate])

  // --- LÓGICA DEFENSIVA PARA CENTROS DE PPE ---
  const { data: rawCentros, isLoading: isLoadingCentros } = useQuery({
    queryKey: ["listaCentrosPPE_Editar"],
    queryFn: async () => {
      try {
        const response = await api.get("/EmplazamientosPPE/getListaCentrosPPE")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Centros:", error)
        return []
      }
    },
  })

  const centrosValidados = Array.isArray(rawCentros)
    ? rawCentros
    : Array.isArray(rawCentros?.data)
      ? rawCentros.data
      : []
  const listaCentros = centrosValidados.map((item) => ({
    id: item.centcodigo || item.id,
    label: item.label || `${item.centcodigo} - ${item.centdescri || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  const {
    mutateAsync: SaveEdicionEmplazamiento,
    isPending: isSavingEdicion,
    isError,
  } = useMutation({
    queryKey: ["isEditingEmplazamiento"],
    fn: async (data) => {
      const response = await api.post("/EmplazamientosPPE/updateEmplazamientosPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // Manejar cambios en los campos del formulario con limpieza y topes
  const handleInputChange = (field, value) => {
    let finalValue = field === "emplstatus" ? value : value.toUpperCase()

    if (field === "emplcodigo") finalValue = finalValue.slice(0, 10)
    else if (field === "empldescri") finalValue = finalValue.slice(0, 100)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Preparar payload tal cual lo espera el backend para la edición de llave compuesta
    const payload = {
      centcodigoOld: centcodigoViejo,
      centcodigoNew: formData.centcodigo,
      emplcodigoOld: emplcodigoViejo,
      emplcodigoNew: formData.emplcodigo,
      empldescri: formData.empldescri,
      emplstatus: formData.emplstatus,
    }

    try {
      if (!payload.centcodigoNew.trim()) return showWarning("Debe seleccionar un Centro de PPE")
      if (!payload.emplcodigoNew.trim()) return showWarning("El Código del Emplazamiento es obligatorio")
      if (!payload.empldescri.trim()) return showWarning("La Descripción del Emplazamiento es obligatoria")

      await SaveEdicionEmplazamiento(payload)
    } catch (error) {
      console.error("Error al editar el Emplazamiento:", error)
      if (error?.code === "VALIDATION_ERROR") {
        showWarning(error.message)
      }
    }
  }

  // Acción dinámica de la barra de menú basada en permisos
  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) =>
      action?.acccaption === "ACTUALIZAR" ||
      action?.acccaption === "GUARDAR" ||
      action?.acccaption === "EDITAR" ||
      action?.acccaption === "EJECUTAR",
  )

  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Toolbar superior con el botón de Guardar/Actualizar */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion || isLoadingCentros}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Emplazamiento de PPE: {emplcodigoViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion || isLoadingCentros} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Emplazamiento
            </Typography>
            <Grid container spacing={3}>
              {/* COMBO CENTRO */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaCentros}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaCentros.find((c) => c.id === formData.centcodigo) ||
                    (formData.centcodigo ? { id: formData.centcodigo, label: `${formData.centcodigo}` } : null)
                  }
                  onChange={(event, newValue) => handleInputChange("centcodigo", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Centro Padre *"
                      placeholder="Buscar centro..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && !formData.centcodigo}
                    />
                  )}
                />
              </Grid>

              {/* CÓDIGO EMPLAZAMIENTO */}
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Cód. Emplazamiento *"
                  value={formData.emplcodigo}
                  onChange={(e) => handleInputChange("emplcodigo", e.target.value)}
                  error={isError && !formData.emplcodigo}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              {/* DESCRIPCIÓN */}
              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.empldescri}
                  onChange={(e) => handleInputChange("empldescri", e.target.value)}
                  error={isError && !formData.empldescri}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* ESTADO (ACTIVO/INACTIVO) */}
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.emplstatus}
                  onChange={(e) => handleInputChange("emplstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarEmplazamientosPPE
